import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Mail, Pencil, Phone, Plus, Search, Trash2, UserRound } from "lucide-react";
import { type Paginated, type SupplierContactDto, type SupplierDto } from "@hisense/shared";
import { PageBody } from "@/components/ui/PageHeader";
import { Button, buttonStyles } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { EmptyTableRow, ListTableFrame, LIST_PAGE_BODY } from "@/components/ui/ListTable";
import { Td, Th, Tr } from "@/components/ui/Table";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useAuth } from "@/app/auth";
import { useCan, useDataRegion } from "@/app/access";
import { SupplierRegionBadge } from "@/components/suppliers/SupplierRegionBadge";
import {
  useDeleteSupplier,
  useSuppliers,
} from "@/api/hooks";
import { useClampListPage, useListQueryState } from "@/lib/listQuery";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { ExportButton } from "@/components/domain/ExportButton";
import { formatDate } from "@/domain/datetime";

/** Show the first few people on the list row; the rest stay behind "+N". */
const CONTACTS_IN_TABLE = 3;

/** Bộ lọc theo đội (29/09/2026) — chỉ cho tài khoản thấy cả hai khu vực; "unclassified" = chưa chọn đội. */
const REGION_FILTERS = ["all", "vietnam", "china", "unclassified"] as const;
type RegionFilter = (typeof REGION_FILTERS)[number];

function ContactsCell({
  contacts,
  total,
}: {
  contacts: SupplierContactDto[];
  total: number;
}) {
  const { t } = useTranslation();
  if (contacts.length === 0) {
    return <span className="text-ink-faint">—</span>;
  }
  const shown = contacts.slice(0, CONTACTS_IN_TABLE);
  const rest = Math.max(total, contacts.length) - shown.length;
  return (
    <ul className="flex min-w-64 flex-col gap-2 py-2">
      {shown.map((c) => (
        <li key={c.id} className="space-y-1 text-xs">
          <div className="flex items-center gap-3 whitespace-nowrap">
            <span className="inline-flex items-center gap-1.5 text-ink" title={t("suppliers.contactName")}>
              <UserRound className="size-3.5 shrink-0 text-ink-soft" aria-hidden="true" />
              <span>{c.name}</span>
            </span>
            {c.phone1 && <span className="inline-flex items-center gap-1.5 text-ink-soft" title={t("suppliers.phone1")}>
              <Phone className="size-3.5 shrink-0" aria-hidden="true" />
              <span className="font-mono tabular">{c.phone1}</span>
            </span>}
          </div>
          {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 whitespace-nowrap text-brand hover:underline" title={t("suppliers.email")}>
            <Mail className="size-3.5 shrink-0 text-ink-soft" aria-hidden="true" />
            <span>{c.email}</span>
          </a>}
          {c.specialization && <p className="pl-5 text-ink-soft">{c.specialization}</p>}
        </li>
      ))}
      {rest > 0 ? (
        <li className="text-2xs text-ink-faint">{t("suppliers.moreContacts", { count: rest })}</li>
      ) : null}
    </ul>
  );
}

export function SuppliersPage() {
  const { t } = useTranslation();
  const query = useListQueryState();
  const { search } = useLocation();
  const { user } = useAuth();
  const can = useCan();
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  // Tài khoản một khu vực chỉ thấy nhà cung cấp của đội mình và nhà cung cấp chưa phân loại — bộ lọc theo đội chỉ dành cho người
  // thấy cả hai, như bộ lọc ở màn Khách hàng.
  const region = useDataRegion();
  const regionParam = query.searchParams.get("classification");
  const regionFilter: RegionFilter = !region && REGION_FILTERS.includes(regionParam as RegionFilter) ? (regionParam as RegionFilter) : "all";
  const classification = regionFilter === "all" ? undefined : regionFilter;

  const { data, isLoading, isFetching, error, refetch } = useSuppliers({
    q: query.q.trim() || undefined,
    page: query.page,
    pageSize: query.pageSize,
    classification,
  });
  useClampListPage(data?.total, query.clampPage);
  const del = useDeleteSupplier();

  const canCreate = user ? can("records:create") : false;
  const canUpdate = user ? can("records:update") : false;
  const canDelete = user ? can("records:delete") : false;

  const rows = data?.data ?? [];
  const rowOffset = ((data?.page ?? query.page) - 1) * (data?.pageSize ?? query.pageSize);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const onDelete = (id: string, name: string) => {
    setDeleteError(null);
    setPendingDelete({ id, name });
  };

  /**
   * Xoá thất bại phải NÓI RA.
   *
   * `del.mutate` nuốt lỗi: hộp thoại đóng lại, dòng vẫn nằm đó, và người dùng
   * kết luận là đã xoá xong. Nhà cung cấp còn sản phẩm gắn vào thì máy chủ từ
   * chối — đó là trường hợp thường gặp nhất, không phải ngoại lệ hiếm.
   */
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await del.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (err) {
      setPendingDelete(null);
      setDeleteError(getLocalizedError(err, t, "suppliers.errorDelete"));
    }
  };

  return (
    <PageBody className={LIST_PAGE_BODY}>
      <ConfirmDialog
        open={!!pendingDelete}
        title={pendingDelete?.name ?? t("common.delete")}
        description={t("suppliers.confirmDelete")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={del.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
      {deleteError && (
        <p className="mt-2 shrink-0 rounded-sm border border-[color:var(--color-danger)]/30 bg-danger-tint px-3 py-2 text-xs text-danger">
          {deleteError}
        </p>
      )}

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Input
          {...query.searchInput}
          leading={<Search />}
          placeholder={t("common.search")}
          aria-label={t("common.search")}
          className="w-full max-w-xs"
        />
        {region ? null : (
          <div className="flex items-center rounded-sm border border-line bg-surface p-0.5">
            {REGION_FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                aria-pressed={regionFilter === f}
                onClick={() => query.updateParam("classification", f === "all" ? null : f)}
                className={
                  "rounded-[3px] px-2.5 py-1 text-xs font-medium transition-colors " +
                  (regionFilter === f ? "bg-brand text-brand-on" : "text-ink-faint hover:text-ink-soft")
                }
              >
                {f === "all" ? t("common.all") : t(`suppliers.${f}`)}
              </button>
            ))}
          </div>
        )}
        <div className="ml-auto">
          <ExportButton<SupplierDto>
            name="suppliers"
            title={t("nav.suppliers")}
            rows={rows}
            total={data?.total}
            disabled={isFetching}
            errorKey="suppliers.errorLoad"
            fetchPage={(page, pageSize, signal) =>
              apiFetch<Paginated<SupplierDto>>("/suppliers", {
                query: { q: query.q.trim() || undefined, page, pageSize, classification },
                signal,
              })
            }
            columns={[
              { header: t("suppliers.company"), value: (s) => s.companyName },
              { header: t("suppliers.classification"), value: (s) => t(s.classification ? `suppliers.${s.classification}` : "suppliers.unclassified") },
              { header: t("suppliers.address"), value: (s) => s.address },
              { header: t("suppliers.phone"), value: (s) => s.phone },
              { header: t("suppliers.email"), value: (s) => s.email },
              { header: t("suppliers.contactCount"), value: (s) => s.contactsCount },
              // Người liên hệ gộp vào MỘT ô "Tên · điện thoại · email", ngăn
              // bằng " | ". Một cột cho mỗi người thì bảng có bao nhiêu cột là
              // do nhà cung cấp nhiều liên hệ nhất quyết định — dán vào sổ nào
              // cũng lệch. Chi tiết đầy đủ nằm ở trang nhà cung cấp.
              {
                header: t("suppliers.contacts"),
                value: (s) =>
                  s.contacts
                    .map((c) => [c.name, c.phone1, c.email].filter(Boolean).join(" · "))
                    .join(" | "),
              },
              { header: t("common.createdOn"), value: (s) => formatDate(s.createdAt) },
              { header: t("common.createdBy"), value: (s) => s.createdByName },
            ]}
          />
        </div>
        {canCreate && (
          <Link to={`/suppliers/new${search}`} className={buttonStyles({ size: "sm" })}>
            <Plus className="size-4" />
            {t("suppliers.newSupplier")}
          </Link>
        )}
      </div>

      <ListTableFrame
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage={t("suppliers.errorLoad")}
        onRetry={() => refetch()}
        page={data?.page ?? query.page}
        pageSize={data?.pageSize ?? query.pageSize}
        total={data?.total ?? 0}
        onPageChange={query.updatePage}
        onPageSizeChange={query.updatePageSize}
      >
            <thead>
              <tr>
                <Th className="w-12" align="right">{t("common.rowNumber")}</Th>
                <Th>{t("suppliers.company")}</Th>
                <Th>{t("suppliers.contacts")}</Th>
                <Th>{t("suppliers.companyContact")}</Th>
                {(canUpdate || canDelete) && <Th align="right" pinRight>{t("common.actions")}</Th>}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <EmptyTableRow colSpan={(canUpdate || canDelete) ? 5 : 4}>{t("common.noMatches")}</EmptyTableRow>
              )}
              {rows.map((s, index) => (
                <Tr key={s.id}>
                  <Td align="right" className="w-12 font-mono tabular text-xs text-ink-faint">{rowOffset + index + 1}</Td>
                  <Td>
                    <div className="w-64 min-w-0 2xl:w-[28rem]">
                      <Link
                        to={`/suppliers/${s.id}${search}`}
                        title={t("suppliers.openSupplier")}
                        className="block whitespace-normal break-words font-medium text-brand hover:underline"
                      >
                        {s.companyName}
                      </Link>
                      <div className="mt-1 flex items-start gap-1.5">
                        <span className="shrink-0"><SupplierRegionBadge classification={s.classification} /></span>
                        {/* Nhà cung cấp riêng tư (28/10/2026): chữ + biểu tượng, không chỉ màu. */}
                        {s.isPrivate ? (
                          <span className="shrink-0"><Badge tone="neutral" title={t("suppliers.privateHint")}><Lock className="size-3" aria-hidden="true" /> {t("suppliers.privateBadge")}</Badge></span>
                        ) : null}
                        <span title={s.address ?? undefined} className="line-clamp-2 whitespace-normal break-words text-xs text-ink-soft">{(s.address ?? "—").replace(/\u00a0/g, " ")}</span>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <ContactsCell contacts={s.contacts} total={s.contactsCount} />
                  </Td>
                  <Td>
                    <div className="flex min-w-52 flex-col gap-1 whitespace-nowrap text-xs">
                      {s.phone ? <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1.5 text-brand hover:underline" title={t("suppliers.phone")}>
                        <Phone className="size-3.5 shrink-0 text-ink-soft" aria-hidden="true" />
                        <span className="font-mono tabular">{s.phone}</span>
                      </a> : null}
                      {s.email ? <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1.5 text-brand hover:underline" title={t("suppliers.email")}>
                        <Mail className="size-3.5 shrink-0 text-ink-soft" aria-hidden="true" />
                        <span>{s.email}</span>
                      </a> : null}
                      {!s.phone && !s.email ? <span className="text-ink-faint">—</span> : null}
                    </div>
                  </Td>
                  {(canUpdate || canDelete) && (
                    <Td align="right" pinRight>
                      <div className="flex items-center justify-end gap-1">
                        {canUpdate && (
                          <Link to={`/suppliers/${s.id}/edit${search}`}
                            state={{ returnToList: true }}
                            className={buttonStyles({ variant: "ghost", size: "icon-sm" })}
                            title={t("common.edit")}
                            aria-label={t("suppliers.editNamed", { name: s.companyName })}>
                            <Pencil className="size-3.5" />
                          </Link>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="icon-sm"
                            onClick={() => onDelete(s.id, s.companyName)}
                            aria-label={t("common.delete")}>
                            <Trash2 className="size-3.5 text-danger" />
                          </Button>
                        )}
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
      </ListTableFrame>
    </PageBody>
  );
}
